// AI 아바타 상담 유입 집계 — 클라이언트/서버 공용
// kind: home(홈페이지) / link(/chat 링크·직접 방문) / embed(다른 사이트에 넣은 삽입 코드)
export type ChatTrack = {
  kind: 'home' | 'link' | 'embed';
  code?: string | null; // 추적 링크 코드 (/chat?src=<code>, 삽입 코드 data-source)
  host?: string | null; // 삽입 코드가 들어간 사이트 주소
  page?: string | null; // 삽입 코드가 들어간 페이지 주소
};

const CODE_RE = /^[a-z0-9-]{3,40}$/;

export function cleanCode(v: unknown) {
  const s = String(v ?? '').trim().toLowerCase();
  return CODE_RE.test(s) ? s : null;
}

// 요청 값 정리 (길이 제한, 허용된 값만)
export function parseTrack(v: unknown): ChatTrack | null {
  const t = v as Partial<ChatTrack> | null;
  if (!t || (t.kind !== 'home' && t.kind !== 'link' && t.kind !== 'embed')) return null;
  return {
    kind: t.kind,
    code: cleanCode(t.code),
    host: t.host ? String(t.host).slice(0, 200) : null,
    page: t.page ? String(t.page).slice(0, 500) : null,
  };
}

// 탭(방문) 단위 식별자: 같은 사람이 여러 번 열어도 방문자 1명으로 센다
export function chatSessionId() {
  try {
    const k = 'avatar_chat_sid';
    let id = sessionStorage.getItem(k);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(k, id);
    }
    return id;
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export function newSourceCode() {
  return Math.random().toString(36).slice(2, 8).padEnd(6, '0');
}

// 사용성 분석용 행동 (event = 'act')
export const CHAT_ACTIONS = [
  'seen', // 상담창이 화면에 실제로 보임
  'sound_on', // 소리 켜고 듣기
  'greeting_end', // 인사 영상을 소리 켜고 끝까지 들음
  'input_focus', // 질문 입력창을 누름
  'compact', // 영상 작게
  'expand', // 영상 크게
  'lead_offer', // 상담 신청 권유가 나옴
  'lead_open', // 무료 상담 신청 버튼을 직접 누름
  'lead_yes', // 권유에 예
  'lead_no', // 권유에 아니오
  'lead_type', // 거래 유형 고름
  'lead_name', // 이름 입력
  'lead_phone', // 연락처 입력
  'lead_kakao', // 카톡/전화 고름 (확인 카드까지 옴)
  'lead_cancel', // 신청 그만하기
  'leave', // 페이지를 떠남/숨김 (act_value = 머문 초)
  'form_lead', // 홈 신청 폼으로 신청
] as const;
export type ChatAction = (typeof CHAT_ACTIONS)[number];
export const isChatAction = (v: unknown): v is ChatAction => (CHAT_ACTIONS as readonly string[]).includes(String(v));

// 행동 기록 보내기 (실패해도 무시). leave 처럼 페이지를 떠날 때는 beacon 으로
export function sendChatAction(track: ChatTrack | null | undefined, action: ChatAction, value?: number) {
  if (!track) return;
  const body = JSON.stringify({ track, session: chatSessionId(), action, value: value ?? null });
  try {
    if (action === 'leave' && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon('/api/avatar-chat/event', new Blob([body], { type: 'application/json' }));
      return;
    }
    void fetch('/api/avatar-chat/event', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch {
    // ignore
  }
}
